import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { getDevices, getDevicePosition, getDeviceHistoryDate, getDrivingTime, getVehicleConfigs, getOdometerToday, VehicleConfigOut, OdometerToday } from '../services/api';
import { Device, Position } from '../types';
import Header from '../components/Layout/Header';
import DeviceList from '../components/DeviceList/DeviceList';
import DeviceDetails from '../components/DeviceDetails/DeviceDetails';
import LiveMap from '../components/Map/LiveMap';
import './Dashboard.css';

const isValidPosition = (data: any): data is Position => {
  return (
    data &&
    typeof data === 'object' &&
    typeof data.latitude === 'number' &&
    typeof data.longitude === 'number' &&
    !('type' in data && 'msg' in data)
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [devices, setDevices] = useState<Device[]>([]);
  const [vehicleConfigs, setVehicleConfigs] = useState<Map<number, VehicleConfigOut>>(new Map());
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [positions, setPositions] = useState<Map<number, Position>>(new Map());
  const [selectedDeviceHistory, setSelectedDeviceHistory] = useState<Position[]>([]);
  const [drivingTime, setDrivingTime] = useState<string>('');
  const [driveStart, setDriveStart] = useState<string | null>(null);
  const [driveEnd, setDriveEnd] = useState<string | null>(null);
  const [odometerToday, setOdometerToday] = useState<OdometerToday | null>(null);
  const [loading, setLoading] = useState(true);
  const [flyToTarget, setFlyToTarget] = useState<{ lat: number; lng: number; ts: number } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileListOpen, setMobileListOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadDevices();
  }, [user, navigate]);

  useEffect(() => {
    if (selectedDeviceId) {
      // wyczyść dane poprzedniego urządzenia, żeby nie migały na nowym
      setDrivingTime('');
      setDriveStart(null);
      setDriveEnd(null);
      setOdometerToday(null);
      setSelectedDeviceHistory([]);
      loadDeviceHistory(selectedDeviceId);
      loadDrivingTime(selectedDeviceId);
      loadOdometerToday(selectedDeviceId);
      setDetailsOpen(true);
      const interval = setInterval(() => {
        loadDevicePosition(selectedDeviceId);
        loadDrivingTime(selectedDeviceId);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [selectedDeviceId]);

  const loadDevices = async () => {
    try {
      const data = await getDevices();
      const validDevices = Array.isArray(data) ? data : [];
      setDevices(validDevices);
      for (const device of validDevices) {
        loadDevicePosition(device.id);
      }
      if (validDevices.length > 0 && !selectedDeviceId) {
        setSelectedDeviceId(validDevices[0].id);
      }
      try {
        const configs = await getVehicleConfigs();
        setVehicleConfigs(new Map(configs.map(c => [c.device_id, c])));
      } catch (err) {
        console.error('Failed to load vehicle configs:', err);
      }
    } catch (error) {
      console.error('Failed to load devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDevicePosition = async (deviceId: number) => {
    try {
      const position = await getDevicePosition(deviceId);
      if (isValidPosition(position)) {
        setPositions(prev => new Map(prev).set(deviceId, position));
      }
    } catch (error) {
      // brak pozycji - ignoruj
    }
  };

  const loadDrivingTime = async (deviceId: number) => {
    try {
      const data = await getDrivingTime(deviceId);
      setDrivingTime(data.driving_time);
      setDriveStart(data.drive_start);
      setDriveEnd(data.drive_end);
    } catch (error) {
      console.error('Failed to load driving time:', error);
    }
  };

  const loadOdometerToday = async (deviceId: number) => {
    try {
      const data = await getOdometerToday(deviceId);
      setOdometerToday(data);
    } catch {
      setOdometerToday(null);
    }
  };

  const loadDeviceHistory = async (deviceId: number) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const history = await getDeviceHistoryDate(deviceId, today);
      setSelectedDeviceHistory(Array.isArray(history) ? history : []);
    } catch (error) {
      console.error('Failed to load device history:', error);
    }
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleSelectDevice = (deviceId: number) => {
    setSelectedDeviceId(deviceId);
    setDetailsOpen(true);
    setMobileListOpen(false);

    // flyTo last known position (all devices, not just Horta)
    const pos = positions.get(deviceId);
    if (!pos) {
      showToast('Brak pozycji dla tego pojazdu');
      return;
    }
    // Skip 0,0 coordinates (Gulf of Guinea guard) and pure 'unknown' coord
    const attrs = (() => { try { return pos.attributes ? JSON.parse(pos.attributes as string) : {}; } catch { return {}; } })();
    const coordSrc: string | undefined = attrs?.coordSource;
    const hasCoords = (pos.latitude !== 0 || pos.longitude !== 0) && coordSrc !== 'unknown';
    if (!hasCoords) {
      showToast('Brak pozycji dla tego pojazdu');
      return;
    }
    setFlyToTarget({ lat: pos.latitude, lng: pos.longitude, ts: Date.now() });
  };

  const selectedDevice = devices.find(d => d.id === selectedDeviceId) || null;
  const selectedPosition = selectedDeviceId ? positions.get(selectedDeviceId) || null : null;

  const mapPositions = (() => {
    if (!searchQuery.trim()) return Array.from(positions.values());
    const q = searchQuery.trim().toLowerCase();
    const visibleIds = new Set(
      devices
        .filter(d =>
          d.name.toLowerCase().includes(q) ||
          (vehicleConfigs.get(d.id)?.registration_number ?? '').toLowerCase().includes(q) ||
          d.uniqueid.toLowerCase().includes(q)
        )
        .map(d => d.id)
    );
    return Array.from(positions.entries())
      .filter(([id]) => visibleIds.has(id))
      .map(([, pos]) => pos);
  })();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Ładowanie danych...</p>
      </div>
    );
  }

  const onlineCount = devices.filter(d => d.status?.trim() === 'online').length;

  return (
    <div className="dashboard-layout">
      <Header />
      <div className="dashboard-body">
        <aside className={`left-sidebar${mobileListOpen ? ' mobile-open' : ''}`}>
          <DeviceList
            devices={devices}
            positions={positions}
            selectedDeviceId={selectedDeviceId}
            onSelectDevice={handleSelectDevice}
            vehicleConfigs={vehicleConfigs}
            query={searchQuery}
            onQueryChange={setSearchQuery}
          />
        </aside>
        <main className="map-area">
          <LiveMap
            positions={selectedDeviceHistory}
            allPositions={mapPositions}
            currentPosition={Array.from(positions.values()).find(p => p.deviceid === selectedDeviceId) || undefined}
            deviceInfo={selectedDevice ? {
              name: selectedDevice.name,
              registration: (selectedDeviceId != null ? vehicleConfigs.get(selectedDeviceId)?.registration_number : null) || undefined,
            } : undefined}
            onMapClick={() => setDetailsOpen(false)}
            flyToTarget={flyToTarget}
          />
          {toast && (
            <div className="map-toast" role="status" aria-live="polite">{toast}</div>
          )}
          <DeviceDetails
            device={selectedDevice}
            position={selectedPosition}
            isOpen={detailsOpen}
            onClose={() => setDetailsOpen(false)}
            vehicleConfig={selectedDeviceId != null ? vehicleConfigs.get(selectedDeviceId) ?? null : null}
            drivingTime={drivingTime}
            driveStart={driveStart}
            driveEnd={driveEnd}
            odometerToday={odometerToday}
          />
          <button
            className="mobile-vehicles-btn"
            onClick={() => setMobileListOpen(o => !o)}
          >
            {mobileListOpen ? '✕ Zamknij' : `☰ Pojazdy (${devices.length})`}
          </button>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
